import { Fragment } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import type { SxProps, Theme } from '@mui/system';
import { colors } from 'Constants/Colors';
import type { EmployeeWithSessionsI, EmployeeSessionRowI } from 'Api/axios/sessions-employees-report-api';
import type { ReportsPaginationI } from 'Pages/reports/components/ReportsDataGrid/types';
import { buildTime } from 'Pages/reports/utils/buildTime';
import { formatDate } from 'Utils/format-date';
import { formatSessionDateTime } from 'Pages/EditCourseSessions/utils/session-datetime';
import { attendancePercentageColor } from 'Pages/EditCourseSessions/utils/session-labels';
import SessionStatusPill from 'Pages/EditCourseSessions/Components/SessionStatusPill';
import EmployeeEvidencesMenu from './EmployeeEvidencesMenu';

interface Props {
  employees: EmployeeWithSessionsI[];
  loading: boolean;
  error: string | null;
  expandedEmployeeId: string | null;
  pagination: ReportsPaginationI | null;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (pageSize: number) => void;
  onToggleExpanded: (id: string) => void;
  onDownloadEvidence: (fileUrl: string, fileName: string) => Promise<void> | void;
  t2: (key: string, ...args: unknown[]) => string;
  emptyText: string;
}

const headerCell: SxProps<Theme> = {
  bgcolor: colors.BLUE_GRAY,
  fontSize: 11,
  fontWeight: 600,
  color: colors.GRAY_DARK,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  py: 1,
};

const subHeaderCell: SxProps<Theme> = {
  bgcolor: colors.GRAY_LIGHT,
  fontSize: 11,
  fontWeight: 600,
  color: colors.GRAY_DARKER,
  py: 0.75,
};

export default function SessionsByEmployeeTable({
  employees,
  loading,
  error,
  expandedEmployeeId,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onToggleExpanded,
  onDownloadEvidence,
  t2,
  emptyText,
}: Props) {
  const history = useHistory();
  if (error) {
    return (
      <Box py={6} textAlign="center">
        <Typography sx={{ color: colors.RED, fontSize: 13 }}>{error}</Typography>
      </Box>
    );
  }

  if (!loading && (!employees || employees.length === 0)) {
    return (
      <Box py={6} textAlign="center">
        <Typography sx={{ color: colors.GRAY_DARK, fontSize: 13 }}>{emptyText}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" aria-label="sesiones por empleado">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCell, width: 40 }} />
              <TableCell sx={headerCell}>{t2('byEmployee.columns.payrollNum')}</TableCell>
              <TableCell sx={headerCell}>{t2('byEmployee.columns.fullName')}</TableCell>
              <TableCell sx={headerCell}>{t2('byEmployee.columns.department')}</TableCell>
              <TableCell sx={headerCell}>{t2('byEmployee.columns.jobTitle')}</TableCell>
              <TableCell align="center" sx={headerCell}>
                {t2('byEmployee.columns.invited')}
              </TableCell>
              <TableCell align="center" sx={headerCell}>
                {t2('byEmployee.columns.present')}
              </TableCell>
              <TableCell align="center" sx={headerCell}>
                {t2('byEmployee.columns.justified')}
              </TableCell>
              <TableCell align="center" sx={headerCell}>
                {t2('byEmployee.columns.absent')}
              </TableCell>
              <TableCell align="center" sx={headerCell}>
                {t2('byEmployee.columns.pending')}
              </TableCell>
              <TableCell align="center" sx={headerCell}>
                {t2('byEmployee.columns.attendancePercentage')}
              </TableCell>
              <TableCell align="center" sx={headerCell}>
                {t2('byEmployee.columns.totalHours')}
              </TableCell>
              <TableCell align="center" sx={headerCell}>
                {t2('byEmployee.columns.lastSession')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {employees.map(emp => {
              const expanded = emp.id === expandedEmployeeId;
              return (
                <Fragment key={emp.id}>
                  <TableRow
                    hover
                    sx={{
                      cursor: 'pointer',
                      '& > *': { borderBottom: expanded ? 'none' : undefined },
                      '&:hover': { bgcolor: colors.custom.table.row.hover },
                    }}
                    onClick={() => onToggleExpanded(emp.id)}
                  >
                    <TableCell>
                      <IconButton size="small" aria-label={expanded ? 'colapsar' : 'expandir'}>
                        {expanded ? (
                          <KeyboardArrowDownIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowRightIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{emp.payrollNum}</TableCell>
                    <TableCell sx={{ fontSize: 13, fontWeight: 500 }}>{emp.fullName}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: colors.GRAY_DARKER }}>{emp.departmentName}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: colors.GRAY_DARKER }}>{emp.jobTitleName}</TableCell>
                    <TableCell align="center" sx={{ fontSize: 13 }}>
                      {emp.totals.invited}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, color: colors.GREEN, fontWeight: 500 }}>
                      {emp.totals.present}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, color: colors.BROWN }}>
                      {emp.totals.justified}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, color: colors.RED }}>
                      {emp.totals.absent}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 13, color: colors.GRAY_DARK }}>
                      {emp.totals.pending}
                    </TableCell>
                    <TableCell align="center">
                      <Box
                        component="span"
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: attendancePercentageColor(emp.totals.attendancePercentage),
                        }}
                      >
                        {emp.totals.attendancePercentage}%
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                      {buildTime(emp.totals.totalHours, t2)}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                      {emp.totals.lastSessionAt ? formatDate(emp.totals.lastSessionAt) : '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={13} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 1, pl: 4, pr: 2 }}>
                          <Table size="small" aria-label="sesiones del empleado">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={subHeaderCell}>{t2('byEmployee.session.columns.datetime')}</TableCell>
                                <TableCell sx={subHeaderCell}>{t2('byEmployee.session.columns.title')}</TableCell>
                                <TableCell sx={subHeaderCell}>{t2('byEmployee.session.columns.course')}</TableCell>
                                <TableCell sx={subHeaderCell}>{t2('byEmployee.session.columns.instructor')}</TableCell>
                                <TableCell align="center" sx={subHeaderCell}>
                                  {t2('byEmployee.session.columns.duration')}
                                </TableCell>
                                <TableCell align="center" sx={subHeaderCell}>
                                  {t2('byEmployee.session.columns.status')}
                                </TableCell>
                                <TableCell align="center" sx={subHeaderCell}>
                                  {t2('byEmployee.session.columns.attendance')}
                                </TableCell>
                                <TableCell sx={subHeaderCell}>{t2('byEmployee.session.columns.comment')}</TableCell>
                                <TableCell align="center" sx={subHeaderCell}>
                                  {t2('byEmployee.session.columns.evidences')}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {emp.sessions.length === 0 && (
                                <TableRow>
                                  <TableCell
                                    colSpan={9}
                                    align="center"
                                    sx={{ color: colors.GRAY_DARK, fontSize: 12.5, py: 2 }}
                                  >
                                    {t2('byEmployee.session.empty')}
                                  </TableCell>
                                </TableRow>
                              )}
                              {emp.sessions.map((s: EmployeeSessionRowI) => (
                                <TableRow
                                  key={s.id}
                                  hover
                                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: colors.custom.table.row.hover } }}
                                  onClick={() => history.push(`/reporte/sesiones/${s.id}`)}
                                >
                                  <TableCell sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                                    {formatSessionDateTime(s.startDateTime)}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 12.5, fontWeight: 500 }}>{s.title}</TableCell>
                                  <TableCell sx={{ fontSize: 12.5, color: colors.GRAY_DARKER }}>
                                    {s.courseName}
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 12.5, color: colors.GRAY_DARKER }}>
                                    {s.instructorName}
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: 12.5, whiteSpace: 'nowrap' }}>
                                    {buildTime(s.durationHours, t2)}
                                  </TableCell>
                                  <TableCell align="center">
                                    <SessionStatusPill status={s.status} size="sm" />
                                  </TableCell>
                                  <TableCell align="center">
                                    <SessionStatusPill status={s.attendance.status} variant="attendance" size="sm" />
                                  </TableCell>
                                  <TableCell sx={{ fontSize: 12.5, color: colors.GRAY_DARKER, maxWidth: 200 }}>
                                    {s.attendance.comment ? (
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          maxWidth: 200,
                                        }}
                                        title={s.attendance.comment}
                                      >
                                        {s.attendance.comment}
                                      </Typography>
                                    ) : (
                                      '—'
                                    )}
                                  </TableCell>
                                  <TableCell align="center" onClick={e => e.stopPropagation()}>
                                    <EmployeeEvidencesMenu
                                      evidences={s.evidences}
                                      onDownload={onDownloadEvidence}
                                      menuLabel={t2('byEmployee.evidences.menuLabel')}
                                      emptyText={t2('byEmployee.evidences.none')}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
        {pagination && (
          <TablePagination
            component="div"
            count={pagination.totalItems}
            page={pagination.page - 1}
            onPageChange={(_, newPage) => onPageChange(newPage + 1)}
            rowsPerPage={pagination.pageSize}
            onRowsPerPageChange={e => onRowsPerPageChange(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
          />
        )}
      </TableContainer>
    </Box>
  );
}
